import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Plus, Edit, Search, Car } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API_URL } from "@/config";

const http = axios.create({ baseURL: API_URL, withCredentials: true });

interface Vehicle {
    vid: number;
    vyear: string | null;
    vmake: string | null;
    vmodel: string | null;
    vtrim: string | null;
    vvin: string | null;
    vcondition: string | null;
    vmileage: string | null;
    vrprice: string | null;
    vdprice: string | null;
    vlivestatus: string | null;
    vfeatured: boolean | null;
    vbody: string | null;
    vthubmnail_file_path: string | null;
    vstockno: string | null;
}

export default function DealerInventoryPage() {
    const { user } = useAuth();
    const dealerId = Number(user?.dealerId ?? 0);

    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (!dealerId) return;
        http.get<Vehicle[]>(`/vehicles/bydealer`, { params: { dealerId } })
            .then(r => setVehicles(r.data))
            .catch(() => setVehicles([]))
            .finally(() => setLoading(false));
    }, [dealerId]);

    const filtered = vehicles.filter(v => {
        const q = search.toLowerCase();
        return (
            !q ||
            v.vmake?.toLowerCase().includes(q) ||
            v.vmodel?.toLowerCase().includes(q) ||
            v.vyear?.includes(q) ||
            v.vvin?.toLowerCase().includes(q) ||
            v.vstockno?.toLowerCase().includes(q)
        );
    });

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <h1 className="text-2xl font-light uppercase tracking-wide">Inventory</h1>
                <div className="flex gap-3">
                    <Link to="/dealer/inventory/add">
                        <Button className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white">
                            <Plus className="h-4 w-4" /> Add Vehicle
                        </Button>
                    </Link>
                </div>
            </div>

            {/* Search */}
            <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                    className="pl-9"
                    placeholder="Search make, model, year, VIN..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            {/* Stats bar */}
            <p className="text-sm text-gray-400">{loading ? "Loading..." : `${filtered.length} vehicle${filtered.length !== 1 ? "s" : ""}`}</p>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="bg-white rounded-2xl shadow p-4 animate-pulse h-40" />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-20">
                    <Car className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-400">{search ? "No vehicles match your search." : "No vehicles in inventory yet."}</p>
                    <Link to="/dealer/inventory/add" className="mt-4 inline-block text-sm text-indigo-600 hover:underline">Add your first vehicle →</Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map(v => (
                        <div key={v.vid} className="bg-white rounded-2xl shadow overflow-hidden flex flex-col">
                            {/* Thumbnail */}
                            <div className="h-40 bg-gray-100 relative">
                                {v.vthubmnail_file_path ? (
                                    <img src={v.vthubmnail_file_path} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <Car className="h-12 w-12 text-gray-300" />
                                    </div>
                                )}
                                <div className="absolute top-2 right-2 flex gap-1">
                                    {v.vfeatured && (
                                        <span className="text-xs bg-amber-400 text-white px-2 py-0.5 rounded-full">Featured</span>
                                    )}
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${v.vlivestatus === "1" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                                        {v.vlivestatus === "1" ? "Live" : "Off"}
                                    </span>
                                </div>
                            </div>

                            {/* Info */}
                            <div className="p-4 flex-1 flex flex-col">
                                <p className="font-semibold">
                                    {[v.vyear, v.vmake, v.vmodel, v.vtrim].filter(Boolean).join(" ") || "Unknown Vehicle"}
                                </p>
                                <p className="text-xs text-gray-400 mt-0.5">
                                    {v.vcondition ?? ""}{v.vmileage ? ` · ${Number(v.vmileage).toLocaleString()} mi` : ""}
                                    {v.vstockno ? ` · #${v.vstockno}` : ""}
                                </p>
                                {v.vvin && <p className="text-xs text-gray-300 font-mono mt-1">{v.vvin}</p>}
                                <div className="mt-auto pt-3 flex items-center justify-between">
                                    <div>
                                        {v.vrprice && (
                                            <p className="font-bold text-indigo-600">${Number(v.vrprice).toLocaleString()}</p>
                                        )}
                                        {v.vdprice && v.vdprice !== v.vrprice && (
                                            <p className="text-xs text-gray-400">Dealer: ${Number(v.vdprice).toLocaleString()}</p>
                                        )}
                                    </div>
                                    <Link to={`/dealer/inventory/edit/${v.vid}`}>
                                        <button className="flex items-center gap-1 text-xs text-indigo-600 hover:underline">
                                            <Edit className="h-3 w-3" /> Edit
                                        </button>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
